
import { defineComponent } from "vue";
import Dataset from "./dataset.vue";
import Folder from "./folder.vue";
import { HierarchySelector } from "./HierarchySelectorHelper";
import { useApiStore } from "@/stores/apiStore";
import { useSiteStore } from "@/stores/siteStore";
import { useToastStore } from "@/stores/toastStore";
import { useImportStore } from "@/stores/importStore";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { useActiveStore } from "@/stores/ssaStore/active";
import { AxiosError } from "axios";
import {
	DatasetType,
	IGetUiHierarchyResponseDTO,
	IParaviewLayersVisPayload,
	ParaviewViewState,
	DatasetObject
} from "@/types/dataset";
import { usePvwStore } from "@/stores/ssaStore/pvw";
import { useViewStore } from "@/stores/ssaStore/views";
import { storeToRefs } from "pinia";
import { useSsaStore } from "@/stores/ssaStore";
import { useEventStore } from "@/stores/eventStore";
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";
import { usePickingStore } from "@/stores/ssaStore/picking";
import {
	BLASTDOG_DATASET_NAME,
	IOGAS_GROUP_NAME,
	CollarDrillholeLayerConfigType,
	NonDrillholeLayerConfigType
} from "@/utils/DisplayPaneConstants";

export default defineComponent({
	setup() {
		const api = useApiStore();
		const siteStore = useSiteStore();
		const importStore = useImportStore();
		const toast = useToastStore();
		const hierarchyStore = useHierarchyStore();
		const pvw = usePvwStore();
		const viewStore = useViewStore();
		const activeStore = storeToRefs(useActiveStore());
		const active = useActiveStore();
		const ssaStore = useSsaStore();
		const eventStore = useEventStore();
		const displayPaneStore = useDisplayPaneStore();
		const pickingStore = usePickingStore();
		const { hierarchyLayerSelections } = storeToRefs(useHierarchyStore());
		return {
			hierarchyLayerSelections,
			active,
			activeStore,
			hierarchyStore,
			api,
			siteStore,
			toast,
			importStore,
			pvw,
			viewStore,
			ssaStore,
			eventStore,
			displayPaneStore,
			pickingStore
		};
	},
	components: {
		Dataset,
		Folder
	},
	name: "imdex-hierarchy",
	data() {
		return {
			activeGroup: "Production",
			activeDatasetType: "",
			activeFolder: null as any,
			highlighted: [] as string[],
			searchTerm: "" as string,
			searchTermTemp: "" as string,
			searchTermDebounce: null as number | null,
			Drillhole: require("@/assets/svg/hole_type2.svg"),
			Blockmodel: require("@/assets/svg/bm_type.svg"),
			Surface: require("@/assets/svg/shape.svg"),
			Points: require("@/assets/svg/polygon_type.svg"),
			confirmDelete: false,
			confirmFolderDelete: false,
			showGroupMenu: false,
			showSubgroupMenu: false,
			closeOptionsMenuListener: null as any,
			loadingDeletingDatasets: false,
			loadingHierarchy: false,
			renamingFolder: false,
			folderNameToDelete: "",
			newFolderName: "",
			confirmMove: false,
			acceptRenameSuggestionModal: false,
			renameSuggestionReason: this.$t(`ssa.hierarchy.move`) as "Move" | "Rename", // type hint - in other languages these values will change but should be consistent
			renameSuggestionTarget: this.$t(`ssa.hierarchy.index.folder`) as "Dataset" | "Folder",
			renameSuggestionFolderDatasetType: "Blockmodel" as DatasetType,
			renameSuggestionOldName: "",
			renameSuggestionNewName: "",
			acceptRenameSuggestionLoading: false,
			folderToMoveTo: "Select",
			groupToMoveTo: "Select",
			foldersInGroup: [] as { text: string; value: string }[],
			subFolderOptions: [] as string[], // replaces foldersInGroup for import, taking the value options from foldersInGroup
			moveDatasetLoading: false,
			newGroupName: "",
			createGroupDialog: false,
			showHierarchyMenu: false,
			createGroupLoading: false,
			deleteGroupLoading: false,
			deleteGroupDialog: false,
			paraviewViewState: {} as ParaviewViewState,
			activeGroups: ["Production"] as any,
			activeSubGroups: [] as any,
			isBlastDogDatasetInProgress: false,
			latestUploadedDatasetType: "",
			latestUploadedDatasetGroupName: "",
			NonDrillholeLayerConfigTypeTmp: NonDrillholeLayerConfigType,
			CollarDrillholeLayerConfigTypeTmp: CollarDrillholeLayerConfigType,
			BD_DS_NAME: BLASTDOG_DATASET_NAME
		};
	},
	async mounted() {
		this.eventStore.init();
		await this.fetchHierarchy();
		this.setIsBlastDogDatasetInProgress();
	},
	computed: {
		hasBlastDogDatasets(): boolean {
			const blastDogData = this.hierarchyStore.hierarchy.filter((group) => {
				return group.name === BLASTDOG_DATASET_NAME;
			});

			if (!blastDogData.length) {
				return false;
			}

			if (!blastDogData[0].children.length) {
				return false;
			}

			for (let i = 0; i < blastDogData[0].children.length; i++) {
				if (blastDogData[0].children[i].datasets?.length) {
					return true;
				}
			}

			return false;
		},
		selected: {
			get(): any {
				if (this.hierarchyStore.hierarchyStates[this.activeStore.view.value] == null) {
					this.hierarchyStore.DATA_SELECT(this.activeStore.view.value, []);
				}
				return (
					this.hierarchyStore.hierarchyStates[this.activeStore.view.value] &&
					this.hierarchyStore.hierarchyStates[this.activeStore.view.value].selected
				);
			},
			set(v: any): any {
				return this.hierarchyStore.DATA_SELECT(this.activeStore.view.value, v);
			}
		},

		isSearching(): boolean {
			return this.searchTerm.length > 0;
		},
		allDatasets(): DatasetObject[] {
			return this.hierarchyStore.ALL_DATASETS() ?? [];
		},
		highlightedDataset(): any {
			return this.active.SSA_ACTIVE_DATASET;
		},
		highlightedLayer(): any {
			return this.activeStore.SSA_ACTIVE_LAYER;
		}
	},
	watch: {
		allDatasets() {
			this.latestUploadedDatasetType = this.eventStore.getLatestUploadedDatasetType;
			this.latestUploadedDatasetGroupName = this.eventStore.getLatestUploadedDatasetGroupName;

			this.toggleGroupOpen(this.latestUploadedDatasetGroupName, true);
			this.toggleSubGroupOpen(
				this.latestUploadedDatasetGroupName + "::" + this.latestUploadedDatasetType,
				true
			);

			this.$nextTick(() => {
				const element = document.getElementById(this.eventStore.getLatestUploadedDatasetId);
				element?.scrollIntoView({ behavior: "smooth" });
			});
		},
		highlightedDataset(v) {
			if (v != null) {
				this.highlighted = [v.id + "..ROOT"];
			} else {
				this.highlighted = [];
			}
		},
		hierarchyLayerSelections: {
			deep: true,
			async handler(v) {
				this.paraviewViewState = v;
			}
		},
		showGroupMenu(v) {
			if (v) {
				this.$nextTick(() => {
					setTimeout(() => {
						window.addEventListener(
							"mousedown",
							(this.closeOptionsMenuListener = () => {
								this.showGroupMenu = false;
								window.removeEventListener(
									"mousedown",
									this.closeOptionsMenuListener
								);
							})
						);
					}, 100);
				});
			}
		},
		showSubgroupMenu(v) {
			if (v) {
				this.$nextTick(() => {
					setTimeout(() => {
						window.addEventListener(
							"mousedown",
							(this.closeOptionsMenuListener = () => {
								this.showSubgroupMenu = false;
								window.removeEventListener(
									"mousedown",
									this.closeOptionsMenuListener
								);
							})
						);
					}, 100);
				});
			}
		},
		async searchTermTemp(v: string) {
			v = v.trim();
			if (v == "") {
				clearTimeout(this.searchTermDebounce || undefined);
				this.searchTerm = "";
				await this.fetchHierarchy();
			} else {
				clearTimeout(this.searchTermDebounce || undefined);
				this.searchTermDebounce = setTimeout(async () => {
					// when search term is set, rerendering thousands of items is very slow, so we fetch hierarchy first with the search term to limit the # of items.
					await this.fetchHierarchy(v);
					this.searchTerm = v;
				}, 1000);
			}
		},
		acceptRenameSuggestionModal(newValue, oldValue) {
			// if the user clicks away in order to reject the suggestion, we need to update the hierarchy as other datasets may have been moved
			if (oldValue && !newValue) this.fetchHierarchy();
		}
	},
	methods: {
		setIsBlastDogDatasetInProgress() {
			const blastDogData = this.hierarchyStore.hierarchy.filter((group) => {
				return group.name === BLASTDOG_DATASET_NAME;
			});

			if (
				this.importStore.isBlastDogDatasetInProgress &&
				(!blastDogData.length ||
					!blastDogData[0].children.length ||
					!blastDogData[0].children[0].datasets?.length)
			) {
				this.isBlastDogDatasetInProgress = true;
				return;
			}

			if (!blastDogData.length || !blastDogData[0].children.length) {
				this.isBlastDogDatasetInProgress = false;
				return;
			}

			let foundInProgress;

			for (let i = 0; i < blastDogData[0].children.length; i++) {
				foundInProgress = blastDogData[0].children[i].datasets?.find((dataset: any) => {
					return dataset.status != "Complete" && dataset.status != "Failed";
				});

				if (foundInProgress) {
					break;
				}
			}

			if (foundInProgress) {
				this.isBlastDogDatasetInProgress = true;
			} else {
				this.isBlastDogDatasetInProgress = false;
			}
		},
		setActiveGroup(groupName: string) {
			this.activeGroup = groupName;
		},
		setDefaultMoveGroup(groupName: string) {
			this.groupToMoveTo = groupName;
			this.getFoldersInGroup(groupName, this.activeDatasetType);
		},
		setActiveDatasetType(datasetType: string) {
			this.activeDatasetType = datasetType;
		},
		toggleGroupOpen(group: any, openOnly = false) {
			if (this.activeGroups.indexOf(group) > -1 && !openOnly) {
				this.activeGroups.splice(this.activeGroups.indexOf(group), 1);
			} else {
				if (this.activeGroups.indexOf(group) == -1) {
					this.activeGroups.push(group);
				}
			}
		},
		toggleSubGroupOpen(group: any, openOnly = false) {
			if (this.activeSubGroups.indexOf(group) > -1 && !openOnly) {
				this.activeSubGroups.splice(this.activeSubGroups.indexOf(group), 1);
			} else {
				if (this.activeSubGroups.indexOf(group) == -1) {
					this.activeSubGroups.push(group);
				}
			}
		},
		importIntoFolder(folder: string) {
			let subgroup = this.activeDatasetType.toLowerCase();
			if (subgroup == "points") {
				subgroup = "point";
			}
			if (subgroup == "surface") {
				subgroup = "shape";
			}

			this.getFoldersInGroup(this.activeGroup, this.activeDatasetType);

			this.importStore.open({
				type: subgroup,
				group: this.activeGroup,
				folderPath: folder,
				subFolderOptions: this.subFolderOptions
			});
		},
		getFoldersInGroup(groupName: string, datasetType: string): any {
			const folders = this.getFolderPathNames(
				this.hierarchyStore.hierarchy,
				groupName,
				datasetType
			); // needs to be updated when adding multiple depth levels
			this.subFolderOptions = folders;
			const seperateLabelsFormat = folders.map((folder) => {
				return { text: folder, value: folder, icon: "fa fa-folder", datasetType: false };
			});

			// Add dataset type to allow removing from folder
			seperateLabelsFormat.unshift({
				text: datasetType,
				value: "",
				icon: datasetType,
				datasetType: true
			});

			this.foldersInGroup = seperateLabelsFormat;
		},
		getFolderPathNames(
			data: IGetUiHierarchyResponseDTO[],
			groupNameFilter: string,
			datasetTypeFilter: string
		): string[] {
			const folderPathNames: string[] = [];

			function traverseObjects(objects: IGetUiHierarchyResponseDTO[]) {
				for (const obj of objects) {
					if (obj.levelType === "folderPath") {
						folderPathNames.push(obj.name);
					}
					if (obj.children && obj.children.length > 0 && obj.levelType === "folderPath") {
						traverseObjects(obj.children);
					} else if (
						obj.children &&
						obj.children.length > 0 &&
						obj.levelType === "groupName" &&
						obj.name == groupNameFilter
					) {
						traverseObjects(obj.children);
					} else if (
						obj.children &&
						obj.children.length > 0 &&
						obj.levelType == "datasetType" &&
						obj.name == datasetTypeFilter
					) {
						traverseObjects(obj.children);
					}
				}
			}

			traverseObjects(data);

			return folderPathNames;
		},
		async createGroup() {
			this.createGroupLoading = true;
			try {
				await this.api.put({
					endpoint: "/dataset/hierarchy/manage",
					post: {
						site: (this.siteStore as any).activeSite.id,
						groupName: this.newGroupName
					}
				});
				await this.fetchHierarchy();
				this.createGroupLoading = false;
				this.createGroupDialog = false;
			} catch (e) {
				this.toast.toast({
					message: "",
					title: this.$t("ssa.hierarchy.index.toastErrorCreatingGroup"),
					type: "error",
					dismissable: false
				});
				this.createGroupLoading = false;
			}
		},
		async deleteGroup() {
			this.deleteGroupLoading = true;
			try {
				await this.api.put({
					endpoint: "/dataset/hierarchy/delete",
					post: {
						site: (this.siteStore as any).activeSite.id,
						groupName: this.activeGroup
					}
				});
				await this.fetchHierarchy();
				this.deleteGroupLoading = false;
				this.deleteGroupDialog = false;
			} catch (e) {
				this.toast.toast({
					message: "",
					title: this.$t("ssa.hierarchy.index.toastErrorDeleteGroup"),
					type: "error",
					dismissable: false
				});
				this.deleteGroupLoading = false;
			}
		},
		async addFolderInto() {
			try {
				await this.api.put({
					endpoint: "/dataset/hierarchy/manage",
					post: {
						site: (this.siteStore as any).activeSite.id,
						groupName: this.activeGroup,
						datasetType: this.activeDatasetType,
						folderPath: this.$t("ssa.hierarchy.index.newFolder"),
						rawFileIds: [],
						datasetIds: []
					}
				});
				await this.fetchHierarchy();
			} catch (e) {
				console.log(`addFolderInto ~ e:`, e);
			}
		},
		importInto(type: string) {
			// #TODO -  we should unify these as DatasetType or make it
			let datasetType: DatasetType = null;
			if (type == "drillhole") {
				datasetType = "Drillhole";
			} else if (type == "points") {
				type = "point";
				datasetType = "Points";
			} else if (type == "point") {
				datasetType = "Points";
			} else if (type == "blockmodel") {
				datasetType = "Blockmodel";
			} else if (type == "surface") {
				type = "shape";
				datasetType = "Surface";
			} else if (type == "shape") {
				datasetType = "Surface";
			} else throw "Invalid type for dataset import";

			this.getFoldersInGroup(this.activeGroup, datasetType ?? "");

			if (this.activeGroup === BLASTDOG_DATASET_NAME) {
				this.setIsBlastDogDatasetInProgress();
			}

			this.importStore.open({
				type: type,
				group: this.activeGroup,
				folderPath: "",
				subFolderOptions: this.subFolderOptions,
				isBlastDogDatasetInProgress: this.isBlastDogDatasetInProgress
			});
		},
		async deleteDataset() {
			this.loadingDeletingDatasets = true;

			const datasets = this.getDatasetsFromHighlighted();
			try {
				for (let i = 0; i < datasets.length; i++) {
					for (
						let p = 0;
						p < Object.keys(datasets[i].paraviewLayersInfoSplit).length;
						p++
					) {
						for (let i2 = 0; i2 < this.viewStore.SSA_VIEW_IDS.length; i2++) {
							this.viewStore.SSA_PARAVIEW_VIEW_DELETE_DATASET(
								this.viewStore.SSA_VIEW_IDS[i2],
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							);
							const hierarchyStateObj = Object.assign(
								{},
								this.hierarchyStore.HIERARCHY_LAYER_SELECTIONS
							);
							if (hierarchyStateObj[this.viewStore.SSA_VIEW_IDS[i2]] == null)
								continue;
							hierarchyStateObj[this.viewStore.SSA_VIEW_IDS[i2]][
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							] = [];
							this.hierarchyStore.HIERARCHY_LAYER_SET(
								this.viewStore.SSA_VIEW_IDS[i2],
								hierarchyStateObj
							);

							this.pvw.PVW_UPDATE_LAYER_VIEW(
								this.viewStore.SSA_VIEW_IDS[i2],
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							);
							this.pvw.PVW_LOAD_DATASET(this.viewStore.SSA_VIEW_IDS[i2], {
								[Object.keys(datasets[i].paraviewLayersInfoSplit)[p]]: []
							});
							this.ssaStore.SSA_UPDATE_ALL_SCALARBARS(
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							);
						}
					}

					await this.api.delete(`/dataset/delete/${datasets[i].id}`);
				}
				this.active.SSA_ACTIVE_LAYER_SET(null);
				this.active.SSA_SET_ACTIVE_DATASET(null);

				this.importStore.setIsBlastDogDatasetInProgress(false);
				this.loadingDeletingDatasets = false;
				this.confirmDelete = false;
				this.fetchHierarchy();
				this.toast.toast({
					message: "",
					title: this.$t("ssa.hierarchy.index.toastSuccessDeletingDs"),
					type: "success",
					dismissable: false
				});
				this.eventStore.clearLatestDatasetId();
			} catch (e) {
				if (e instanceof AxiosError) {
					this.toast.toast({
						message: e.response!.data.message,
						title: this.$t("ssa.hierarchy.index.toastErrorDeletingDs"),
						type: "error",
						dismissable: false
					});
					this.loadingDeletingDatasets = false;
					this.eventStore.clearLatestDatasetId();
				}
			}
		},
		async deleteFolder() {
			// TODO
			this.loadingDeletingDatasets = true;
			const datasets = this.getDatasetsFromFolder(this.folderNameToDelete);

			try {
				const datasetIdsToDelete: string[] = [];

				for (let i = 0; i < datasets.length; i++) {
					for (
						let p = 0;
						p < Object.keys(datasets[i].paraviewLayersInfoSplit).length;
						p++
					) {
						for (let i2 = 0; i2 < this.viewStore.SSA_VIEW_IDS.length; i2++) {
							this.viewStore.SSA_PARAVIEW_VIEW_DELETE_DATASET(
								this.viewStore.SSA_VIEW_IDS[i2],
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							);

							const hierarchyStateObj = Object.assign(
								{},
								this.hierarchyStore.HIERARCHY_LAYER_SELECTIONS
							);

							if (hierarchyStateObj[this.viewStore.SSA_VIEW_IDS[i2]] == null)
								continue;
							hierarchyStateObj[this.viewStore.SSA_VIEW_IDS[i2]][
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							] = [];
							this.hierarchyStore.HIERARCHY_LAYER_SET(
								this.viewStore.SSA_VIEW_IDS[i2],
								hierarchyStateObj
							);

							this.pvw.PVW_UPDATE_LAYER_VIEW(
								this.viewStore.SSA_VIEW_IDS[i2],
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							);
							this.pvw.PVW_LOAD_DATASET(this.viewStore.SSA_VIEW_IDS[i2], {
								[Object.keys(datasets[i].paraviewLayersInfoSplit)[p]]: []
							});
							this.ssaStore.SSA_UPDATE_ALL_SCALARBARS(
								Object.keys(datasets[i].paraviewLayersInfoSplit)[p]
							);
						}
					}

					datasetIdsToDelete.push(datasets[i].id);
				}

				if (datasetIdsToDelete.length) {
					await this.api.delete("/dataset/delete", { ids: datasetIdsToDelete });
				}

				this.active.SSA_ACTIVE_LAYER_SET(null);
				this.active.SSA_SET_ACTIVE_DATASET(null);

				const postData = {
					groupName: this.activeGroup,
					datasetType: this.activeDatasetType,
					folderPath: this.folderNameToDelete,
					site: (this.siteStore as any).activeSite.id
				};
				await this.api.put({ endpoint: `/dataset/hierarchy/delete`, post: postData });

				this.confirmFolderDelete = false;
				this.folderNameToDelete = "";
				this.loadingDeletingDatasets = false;
				this.fetchHierarchy();
				this.toast.toast({
					message: "",
					title: this.$t("ssa.hierarchy.index.toastSuccessDeletingFolder"),
					type: "success",
					dismissable: false
				});
				this.eventStore.clearLatestDatasetId();
			} catch (e) {
				if (e instanceof AxiosError) {
					this.toast.toast({
						message: e.response!.data.message,
						title: this.$t("ssa.hierarchy.index.toastErrorDeletingFolder"),
						type: "error",
						dismissable: false
					});
					this.loadingDeletingDatasets = false;
					this.folderNameToDelete = "";
					this.eventStore.clearLatestDatasetId();
				}
			}
		},
		async moveDataset(forceRefresh = false) {
			const datasets = this.getDatasetsFromHighlighted();
			this.moveDatasetLoading = true;
			const postData = {
				site: (this.siteStore as any).activeSite.id,
				groupName: this.groupToMoveTo,
				datasetType: this.activeDatasetType,
				folderPath: this.folderToMoveTo,
				datasetIds: datasets.map((item: any) => item.id)
			};
			try {
				const { data } = await this.api.put({
					endpoint: `/dataset/hierarchy/manage`,
					post: postData
				});
				if (data.suggestedName) {
					if (forceRefresh && typeof forceRefresh === "boolean") this.fetchHierarchy();
					// disable current dialog
					this.moveDatasetLoading = false;
					this.confirmMove = false;
					// suggest a rename
					this.renameSuggestionReason = this.$t(`ssa.hierarchy.move`) as "Move";
					this.renameSuggestionTarget = this.$t(
						`ssa.hierarchy.index.dataset`
					) as "Dataset";
					this.renameSuggestionOldName = data.oldName;
					this.renameSuggestionNewName = data.suggestedName;
					this.$nextTick(() => {
						this.acceptRenameSuggestionModal = true;
					});
					return;
				} else {
					if (datasets.length > 0)
						this.eventStore.latestUploadedDatasetId = datasets?.[0]?.id; // any one of the datasets

					this.eventStore.latestUploadedDatasetFolderPath = this.folderToMoveTo;
					this.eventStore.latestUploadedDatasetType = this.activeDatasetType;
					this.eventStore.latestUploadedDatasetGroupName = this.groupToMoveTo;

					this.moveDatasetLoading = false;
					this.fetchHierarchy();
					this.confirmMove = false;
					this.folderToMoveTo = "Select";
					setTimeout(() => {
						this.eventStore.clearLatestDatasetId();
						this.eventStore.clearLatestDatasetType();
					}, 750); // increased to 750 as remotely the move/fetch operation is much slower - worst case it fails to open the modified dataset
					// return;
				}
			} catch (e) {
				if (e instanceof AxiosError) {
					this.toast.toast({
						message: e.response!.data.message,
						title: this.$t("ssa.hierarchy.index.toastErrorMovingDs"),
						dismissable: false,
						type: "error"
					});
					this.moveDatasetLoading = false;
					this.confirmMove = false;
					this.eventStore.clearLatestDatasetId();
				}
				return;
			}
		},
		async renameFolder({
			newFolderPath,
			folderPath,
			datasetType
		}: {
			newFolderPath: string;
			folderPath: string;
			datasetType: string;
		}) {
			if (!newFolderPath || !newFolderPath.trim() || newFolderPath === folderPath) return;
			try {
				const postData = {
					newFolderPath,
					folderPath,
					groupName: this.activeGroup,
					site: (this.siteStore as any).activeSite.id,
					datasetType: datasetType
				};
				const { data } = await this.api.put({
					endpoint: "/dataset/hierarchy/rename",
					post: postData
				});
				if (data.suggestedName) {
					// suggest a rename
					this.renameSuggestionReason = this.$t(`ssa.hierarchy.rename`) as "Rename";
					this.renameSuggestionTarget = this.$t(`ssa.hierarchy.index.folder`) as "Folder";
					this.renameSuggestionOldName = postData.folderPath;
					this.renameSuggestionNewName = data.suggestedName;
					this.renameSuggestionFolderDatasetType = postData.datasetType as DatasetType;
					this.acceptRenameSuggestionModal = true;
					return;
				} else {
					this.toast.toast({
						message: "",
						title: this.$t("ssa.hierarchy.index.toastSuccessRenameFolder"),
						dismissable: false,
						type: "success"
					});
					this.fetchHierarchy();
					this.eventStore.clearLatestDatasetId();
					return;
				}
			} catch (e) {
				if (e instanceof AxiosError) {
					this.toast.toast({
						message: e.response!.data.message,
						title: this.$t("ssa.hierarchy.index.toastErrorRenameFolder"),
						dismissable: false,
						type: "error"
					});
					this.eventStore.clearLatestDatasetId();
				} else {
					this.toast.toast({
						message: this.$t("ssa.hierarchy.index.toastErrorRenameFolderMsg"),
						title: this.$t("ssa.hierarchy.index.toastErrorRenameFolder"),
						dismissable: false,
						type: "error"
					});
					this.eventStore.clearLatestDatasetId();
				}
			}
		},
		async renameDataset(data: any, oldDatasetName: string, skipRefresh = false) {
			const datasets = this.getDatasetsFromHighlighted();

			if (data && datasets[0].id != data.data.datasetId) {
				return;
			}

			const newName = data?.data?.name;
			let datasetToBeRenamed = datasets[0];
			const postData = {
				datasetId: datasetToBeRenamed.id,
				newName
			};
			if (oldDatasetName && skipRefresh) {
				// renaming based on suggestion, there may be multiple datasets highlighted, and all of the highlighted names must end up unique
				const dataset = datasets.find((dataset) => dataset.name === oldDatasetName);
				if (dataset) {
					postData.datasetId = dataset.id;
					datasetToBeRenamed = dataset;
				}
			}
			if (!newName || !newName.trim() || datasetToBeRenamed.name === newName) return;
			try {
				const { data } = await this.api.put({
					endpoint: "/dataset/hierarchy/rename",
					post: postData
				});
				if (data.suggestedName) {
					this.renameSuggestionReason = this.$t(`ssa.hierarchy.rename`) as "Rename";
					this.renameSuggestionTarget = this.$t(
						`ssa.hierarchy.index.dataset`
					) as "Dataset";
					this.renameSuggestionOldName = datasets[0].name; // datasetToBeRenamed.name ?
					this.renameSuggestionNewName = data.suggestedName;
					this.acceptRenameSuggestionModal = true;
				} else {
					this.toast.toast({
						message: "",
						title: this.$t("ssa.hierarchy.index.toastSuccessRenameDs"),
						dismissable: false,
						type: "success"
					});
					if (!skipRefresh) {
						if (datasets.length > 0)
							this.eventStore.latestUploadedDatasetId = datasetToBeRenamed.id;

						this.eventStore.latestUploadedDatasetFolderPath =
							data.datasetHierarchy.folderPath;
						this.eventStore.latestUploadedDatasetType =
							data.datasetHierarchy.datasetType;
						this.eventStore.latestUploadedDatasetGroupName =
							data.datasetHierarchy.groupName;
						await this.fetchHierarchy();

						setTimeout(() => {
							this.eventStore.clearLatestDatasetId();
							this.eventStore.clearLatestDatasetType();
						}, 300);

						// Push update to all views in active store (and thus display pane)
						const newDataset = this.allDatasets.find(
							(d: DatasetObject) => d.name === newName
						);
						if (newDataset) {
							this.active.SSA_UPDATE_DATASET_IN_VIEWS(newDataset);
						}
					}
				}
			} catch (e) {
				if (e instanceof AxiosError) {
					this.toast.toast({
						message: e.response!.data.message,
						title: this.$t("ssa.hierarchy.index.toastErrorRenameDs"),
						dismissable: false,
						type: "error"
					});
					this.eventStore.clearLatestDatasetId();
				} else {
					this.toast.toast({
						message: this.$t("ssa.hierarchy.index.toastErrorRenameDsMsg"),
						title: this.$t("ssa.hierarchy.index.toastErrorRenameDs"),
						dismissable: false,
						type: "error"
					});
					this.eventStore.clearLatestDatasetId();
				}
			}
		},
		async acceptRenameSuggestion() {
			try {
				this.acceptRenameSuggestionLoading = true;

				if (
					this.renameSuggestionTarget ===
					(this.$t(`ssa.hierarchy.index.dataset`) as "Dataset")
				) {
					await this.renameDataset(
						{ data: { name: this.renameSuggestionNewName } },
						this.renameSuggestionOldName,
						this.renameSuggestionReason === (this.$t(`ssa.hierarchy.move`) as "Move")
					);
				} else {
					await this.renameFolder({
						newFolderPath: this.renameSuggestionNewName,
						folderPath: this.renameSuggestionOldName,
						datasetType: this.renameSuggestionFolderDatasetType ?? ""
					});
				}

				// If the reason for this rename is a move, move after renaming
				if (this.renameSuggestionReason === (this.$t(`ssa.hierarchy.move`) as "Move")) {
					await this.moveDataset(true);
				}
				this.acceptRenameSuggestionModal = false;
				this.acceptRenameSuggestionLoading = false;
			} catch (e: any) {
				console.log(`e:`, e);
				this.acceptRenameSuggestionModal = false;
				this.acceptRenameSuggestionLoading = false;
			}
		},
		getDatasetsFromHighlighted() {
			let datasets: DatasetObject[] = this.allDatasets;

			// limits to deleting one type of dataset at a time

			datasets = datasets.filter(
				(dataset) =>
					this.highlighted.map((item) => item.split("..")[0]).includes(dataset.id) && {
						...dataset
					}
			);

			return datasets;
		},
		clearHighlightedDatasets() {
			this.highlighted = [];
		},
		getDatasetsFromFolder(folder: string) {
			const activeGroup = this.hierarchyStore.hierarchy.filter((item: any) => {
				return item.name == this.activeGroup;
			});

			let datasets = [] as any[];
			const collectDatasets = (children: any[]) => {
				for (let i = 0; i < children.length; i++) {
					if (children[i].datasets != null) {
						if (children[i].parent == folder || children[i].name == folder) {
							datasets = datasets.concat(
								children[i].datasets.map((item: any) => {
									return { ...item, parent: children[i].name };
								})
							);
						}
					}
					if (children[i].children != null && children[i].children.length > 0) {
						collectDatasets(children[i].children);
					}
				}
			};
			collectDatasets(
				activeGroup[0].children.filter((item: any) => item.name == this.activeDatasetType)
			);
			return datasets;
		},
		toggleGroupRightClickMenu(type?: string, e?: any) {
			const [y, x] = [e.pageY, e.pageX];
			this.showGroupMenu = !this.showGroupMenu;

			this.$nextTick(() => {
				if (this.showGroupMenu) {
					const body = document.getElementsByTagName("body")[0];
					(this.$refs as any).showGroupMenu.style.top = y + 5 + "px";
					(this.$refs as any).showGroupMenu.style.left = x + "px";
					body.appendChild((this.$refs as any).showGroupMenu);
				}
			});
		},
		toggleSubgroupRightClickMenu(type?: string, e?: any) {
			const [y, x] = [e.pageY, e.pageX];
			this.showSubgroupMenu = !this.showSubgroupMenu;

			this.$nextTick(() => {
				if (this.showSubgroupMenu) {
					const body = document.getElementsByTagName("body")[0];
					(this.$refs as any).showSubgroupMenu.style.top = y + 5 + "px";
					(this.$refs as any).showSubgroupMenu.style.left = x + "px";
					body.appendChild((this.$refs as any).showSubgroupMenu);
				}
			});
		},
		async fetchHierarchy(searchTerm = "") {
			this.loadingHierarchy = true;
			try {
				await this.hierarchyStore.fetchHierarchy(
					typeof searchTerm == "string" && searchTerm ? searchTerm : this.searchTerm
				);
				this.loadingHierarchy = false;
			} catch (e) {
				this.loadingHierarchy = false;
			}
		},
		async dataSelect({
			cellUniqueId,
			paraviewVisHierarchies,
			type,
			handleUnselectedChild,
			dataType
		}: {
			cellUniqueId: string;
			paraviewVisHierarchies: string[];
			type: "on" | "off";
			handleUnselectedChild: boolean;
			dataType: string;
			depth: number;
		}) {
			if (!cellUniqueId) return;
			this.dataHighlight({
				datasetId: cellUniqueId.split("::")[0],
				type: "",
				dataType
			});
			await setTimeout(() => {
				return;
			}, 100);
			if (!this.activeStore.view) return; // handle error if no activeViewId

			const dataset = this.allDatasets.find(
				(dataset) => dataset.id == cellUniqueId.split("..")[0]
			);
			if (!dataset) {
				return;
			}

			if (type === "off") {
				// this handles the edge case where a child is unselected, when it was selected by something much higher in the tree, so we need to select all the siblings individually
				if (
					handleUnselectedChild &&
					this.selected.filter((item: string) => item.includes(cellUniqueId)).length === 0
				) {
					const datasetVisHierarchies = Object.values(
						dataset.paraviewLayersInfoSplit
					).find(
						(item) =>
							item.layerConfigType === NonDrillholeLayerConfigType ||
							item.layerConfigType === CollarDrillholeLayerConfigType
					)?.hierarchies;

					if (!datasetVisHierarchies) return;

					const hierarchiesDatasetPrefixed = datasetVisHierarchies.map(
						(item) => cellUniqueId.split("..")[0] + ".." + item
					);
					// # todo - move management to this class for all outcomes  with helper functions
					const newSelected = HierarchySelector.deselect(
						hierarchiesDatasetPrefixed,
						this.selected,
						cellUniqueId
					);
					this.selected = [...newSelected];
				} else {
					// this is the standard approach; simply remove the unique id from our selections array
					this.selected = [...this.selected].filter((item: any) => {
						const split = cellUniqueId.split("::");
						const hSplit = item.split("::");
						let check = true;
						for (let s = 0; s < split.length; s++) {
							if (hSplit[s] != split[s]) {
								check = false;
							}
						}
						return !check;
					});
				}

				const newSmallerParaviewLayersInfo =
					this.paraviewViewState[this.activeStore.view.value] ?? {};

				for (const paraviewLayerKey of Object.keys(dataset.paraviewLayersInfoSplit)) {
					if (
						newSmallerParaviewLayersInfo[paraviewLayerKey] &&
						newSmallerParaviewLayersInfo[paraviewLayerKey].length > 0
					) {
						const filteredHierarchies = newSmallerParaviewLayersInfo[
							paraviewLayerKey
						].filter(
							(hierarchyString) => !paraviewVisHierarchies.includes(hierarchyString)
						);
						newSmallerParaviewLayersInfo[paraviewLayerKey] = filteredHierarchies;
					} else {
						newSmallerParaviewLayersInfo[paraviewLayerKey] = [];
					}
				}
				this.paraviewViewState[this.activeStore.view.value] =
					newSmallerParaviewLayersInfo ?? {};
				this.viewStore.SSA_SET_PARAVIEW_VIEW_STATE(
					this.activeStore.view.value,
					newSmallerParaviewLayersInfo
				);

				this.hierarchyStore.HIERARCHY_LAYER_SET(
					this.activeStore.view.value,
					this.paraviewViewState[this.activeStore.view.value]
				);

				this.active.SSA_ACTIVE_LAYER_SET(null);
				// for (const key of Object.keys(this.paraviewViewState)) {
				// 	await this.pvw.PVW_UPDATE_LAYER_VIEW(this.activeStore.view.value, key);  // This is causing each layer to activate and causing too many re-renders due to watchers. Don't know why this is here.
				// }
			} else {
				this.selected = [...this.selected, cellUniqueId];
				// only send the visualization if paraviewLayersInfo has content, since it is always the visualization payload sent once
				if (
					dataset.paraviewLayersInfoSplit &&
					Object.keys(dataset.paraviewLayersInfoSplit).length > 0
				) {
					const newLargerParaviewLayersInfo: IParaviewLayersVisPayload =
						this.paraviewViewState[this.activeStore.view.value] ?? {};
					//
					for (const paraviewLayerKey of Object.keys(dataset.paraviewLayersInfoSplit)) {
						if (!newLargerParaviewLayersInfo[paraviewLayerKey])
							newLargerParaviewLayersInfo[paraviewLayerKey] = []; // if no layers are currently loaded, add the key as an empty []
						newLargerParaviewLayersInfo[paraviewLayerKey] = [
							...new Set([
								...paraviewVisHierarchies,
								...newLargerParaviewLayersInfo[paraviewLayerKey]
							])
						]; // merge the previously visualized items with the currently visualized items, and uses a Set to remove duplicates
					}
					this.paraviewViewState[this.activeStore.view.value] =
						newLargerParaviewLayersInfo;
					this.viewStore.SSA_SET_PARAVIEW_VIEW_STATE(
						this.activeStore.view.value,
						newLargerParaviewLayersInfo
					);

					this.hierarchyStore.HIERARCHY_LAYER_SET(
						this.activeStore.view.value,
						this.paraviewViewState[this.activeStore.view.value]
					);
				}
			}

			const highlightedDataset = this.allDatasets.filter((item) => {
				return item.id == cellUniqueId.split("::")[0].split("..")[0];
			});

			this.active.SSA_SET_DATASETS_SELECTED(
				highlightedDataset[0],
				type == "off",
				this.selected
			);
		},
		dataHighlight({
			datasetId,
			type,
			dataType
		}: {
			datasetId: string;
			type?: string;
			dataType?: string;
		}) {
			this.pickingStore.SHOW_PICK_DATA(false);

			if (type == "shift") {
				if (this.highlighted.length === 0) {
					// if no items are highlighted, we can stop and just add the new item
					this.highlighted = [datasetId];
					return;
				} else {
					const firstHighlightedDataset = this.allDatasets.filter((item) => {
						return item.id == this.highlighted[0].split("..")[0];
					});
					if (firstHighlightedDataset[0].dataType != dataType) {
						// if dataset types don't match, empty the current highlighted and replace with this item
						// if they do match, we continue as normal
						this.highlighted = [datasetId];
						return;
					}
				}

				// check if item already exists
				let matchFound = false;
				for (let i = 0; i < this.highlighted.length; i++) {
					if (this.highlighted[i] === datasetId) {
						this.highlighted.splice(i, 1);
						matchFound = true;
						break;
					}
				}
				if (!matchFound) {
					this.highlighted.push(datasetId);
				}
			} else {
				const highlightedDataset = this.allDatasets.filter((item) => {
					return item.id == datasetId.split("..")[0];
				});

				if (highlightedDataset.length > 0) {
					this.active.SSA_SET_ACTIVE_DATASET(highlightedDataset[0]);
				}

				this.highlighted = [datasetId];

				const foundSelected =
					this.selected &&
					this.selected.find((item: any) => {
						return item.indexOf(datasetId) > -1;
					});

				if (foundSelected) {
					if (
						!this.displayPaneStore.DISPLAY_STATES[this.activeStore.view.value][
							datasetId.split("..")[0]
						]
					) {
						console.log(` ~ Check DISPLAY_STATES, error highlighting`);
						return;
					}
					const activeLayer = this.displayPaneStore.DISPLAY_STATES[
						this.activeStore.view.value
					][datasetId.split("..")[0]].find((layer: any) => {
						return layer.selected && layer.open;
					});

					if (activeLayer) {
						this.active.SSA_ACTIVE_LAYER_SET(activeLayer.layerName);
						const layerName = activeLayer.layerName;
						const viewId = this.activeStore.view.value;
						let fieldList: any = ["hole_name", "bench_name", "pattern_name"];
						if (
							activeLayer.configuration.length > 0 &&
							activeLayer.configuration[0].availableProperties
						) {
							fieldList = fieldList.concat(
								activeLayer.configuration[0].availableProperties[0].children.map(
									(item: any) => {
										return item.value;
									}
								)
							);
						}

						if (
							!activeLayer.name.toLowerCase().includes("survey") &&
							!activeLayer.name.toLowerCase().includes("slic")
						) {
							this.pvw.PVW_SET_FIELD_LIST({ layerName, viewId, fieldList });
						}
					}
				}
			}
		},
		async handleRefresh() {
			if (this.searchTermTemp === "") {
				await this.fetchHierarchy();
			} else if (this.searchTermTemp != this.searchTerm) {
				await this.fetchHierarchy();
			} else {
				this.searchTermTemp = "";
			}
		}
	}
});
